import { ApiClient } from '../api/client';

export interface User {
  id: string;
  email: string;
  name: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
}

export class AuthManager {
  private static instance: AuthManager;
  private authState: AuthState = {
    isAuthenticated: false,
    user: null,
    token: null
  };
  private apiClient: ApiClient;
  private listeners: ((state: AuthState) => void)[] = [];

  private constructor() {
    // Use environment variable for API URL, fallback to relative path for development
    const apiUrl = import.meta.env.VITE_API_URL || '/api';
    this.apiClient = new ApiClient(apiUrl);
    this.loadStoredAuth();
  }

  public static getInstance(): AuthManager {
    if (!AuthManager.instance) {
      AuthManager.instance = new AuthManager();
    }
    return AuthManager.instance;
  }

  private loadStoredAuth(): void {
    const token = localStorage.getItem('auth_token');
    const userStr = localStorage.getItem('auth_user');
    
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        this.authState = {
          isAuthenticated: true,
          user,
          token
        };
        this.notifyListeners();
      } catch (error) {
        console.error('Failed to parse stored user data:', error);
        this.clearStoredAuth();
      }
    }
  }

  private storeAuth(token: string, user: User): void {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_user', JSON.stringify(user));
  }

  private clearStoredAuth(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.authState));
  }

  public subscribe(listener: (state: AuthState) => void): () => void {
    this.listeners.push(listener);
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  public getAuthState(): AuthState {
    return { ...this.authState };
  }

  public async login(email: string, password: string): Promise<void> {
    try {
      const response = await this.apiClient.post<{ user: User; token: string }>('/auth/login', {
        email,
        password
      });

      this.authState = {
        isAuthenticated: true,
        user: response.user,
        token: response.token
      };

      this.storeAuth(response.token, response.user);
      this.notifyListeners();
    } catch (error) {
      throw new Error('Login failed. Please check your credentials.');
    }
  }

  public async signup(name: string, email: string, password: string): Promise<void> {
    try {
      const response = await this.apiClient.post<{ user: User; token: string }>('/auth/signup', {
        name,
        email,
        password
      });

      this.authState = {
        isAuthenticated: true,
        user: response.user,
        token: response.token
      };

      this.storeAuth(response.token, response.user);
      this.notifyListeners();
    } catch (error) {
      throw new Error('Signup failed. Please try again.');
    }
  }

  public logout(): void {
    this.authState = {
      isAuthenticated: false,
      user: null,
      token: null
    };

    this.clearStoredAuth();
    this.notifyListeners();
  }

  public getAuthHeaders(): Record<string, string> {
    if (this.authState.token) {
      return {
        'Authorization': `Bearer ${this.authState.token}`
      };
    }
    return {};
  }
}
